import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, inputCls, Skeleton, EmptyState, StatusPill } from './shared';

interface AdvertiserRow {
  id: string;
  name: string;
  company_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  default_postback_url: string | null;
  default_postback_method: string;
  notes: string | null;
  is_active: boolean;
  campaignCount: number;
}

interface CampaignRow {
  id: string;
  revenue?: number;
  spend?: number;
  roi?: number;
  source_id?: string | null;
}

const EMPTY_FORM = {
  name: '', companyName: '', contactName: '', contactEmail: '',
  defaultPostbackUrl: '', defaultPostbackMethod: 'GET', notes: '',
};

export default function AdvertisersSection({ onSelect }: { onSelect: (id: string) => void }) {
  const qc = useQueryClient();
  const advertisersQ = useQuery({
    queryKey: ['admin', 'ua', 'sources'],
    queryFn: () => apiFetch('/api/ua/admin/sources') as Promise<{ sources: AdvertiserRow[] }>,
  });
  // We pull campaigns too so we can roll up lifetime revenue/spend per advertiser
  // without needing a second round-trip per row. The campaigns endpoint already
  // returns source_id + enriched metrics.
  const campaignsQ = useQuery({
    queryKey: ['admin', 'ua', 'campaigns'],
    queryFn: () => apiFetch('/api/ua/admin/campaigns') as Promise<{ campaigns: Array<CampaignRow & { source_id: string | null }> }>,
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const advertisers = advertisersQ.data?.sources ?? [];
  const campaigns = campaignsQ.data?.campaigns ?? [];

  // Roll up totals per advertiser from the already-loaded campaigns.
  const totalsByAdvertiser = new Map<string, { revenue: number; spend: number }>();
  for (const c of campaigns) {
    if (!c.source_id) continue;
    const prev = totalsByAdvertiser.get(c.source_id) ?? { revenue: 0, spend: 0 };
    prev.revenue += c.revenue ?? 0;
    prev.spend += c.spend ?? 0;
    totalsByAdvertiser.set(c.source_id, prev);
  }

  function startEdit(a: AdvertiserRow) {
    setEditId(a.id);
    setForm({
      name: a.name,
      companyName: a.company_name ?? '',
      contactName: a.contact_name ?? '',
      contactEmail: a.contact_email ?? '',
      defaultPostbackUrl: a.default_postback_url ?? '',
      defaultPostbackMethod: a.default_postback_method ?? 'GET',
      notes: a.notes ?? '',
    });
  }

  async function handleSave(isNew: boolean) {
    setSaving(true);
    try {
      if (isNew) await apiFetch('/api/ua/admin/sources', { method: 'POST', body: JSON.stringify(form) });
      else if (editId) await apiFetch(`/api/ua/admin/sources/${editId}`, { method: 'PATCH', body: JSON.stringify(form) });
      setEditId(null);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      void qc.invalidateQueries({ queryKey: ['admin', 'ua', 'sources'] });
    } catch { /* surfaced via query error */ }
    setSaving(false);
  }

  async function handleToggle(id: string, active: boolean) {
    try {
      await apiFetch(`/api/ua/admin/sources/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive: !active }) });
      void qc.invalidateQueries({ queryKey: ['admin', 'ua', 'sources'] });
    } catch { /* ignore */ }
  }

  const modal = (editId || showCreate) ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setEditId(null); setShowCreate(false); }}>
      <div className="bg-slate-800 rounded-xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-white">{showCreate ? 'Create Advertiser' : 'Edit Advertiser'}</h3>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Advertiser name *" className={inputCls + ' w-full'} />
        <input value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} placeholder="Company" className={inputCls + ' w-full'} />
        <div className="flex gap-2">
          <input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} placeholder="Contact name" className={inputCls + ' flex-1'} />
          <input value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} placeholder="Email" className={inputCls + ' flex-1'} />
        </div>
        <input value={form.defaultPostbackUrl} onChange={(e) => setForm({ ...form, defaultPostbackUrl: e.target.value })} placeholder="Default postback URL ({click_id} {member_id} etc)" className={inputCls + ' w-full font-mono text-xs'} />
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows={2} className={inputCls + ' w-full resize-none'} />
        <div className="flex gap-2 justify-end">
          <button onClick={() => { setEditId(null); setShowCreate(false); }} className="text-slate-400 text-sm px-3 py-1">Cancel</button>
          <button onClick={() => void handleSave(showCreate)} disabled={!form.name || saving} className="bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (advertisersQ.isLoading) {
    return <Skeleton className="h-60" />;
  }

  return (
    <div className="space-y-4">
      {modal}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-bold text-white">Advertisers</h3>
        <button
          onClick={() => { setShowCreate(true); setForm(EMPTY_FORM); }}
          className="bg-primary text-white px-4 py-1.5 rounded-lg text-xs font-medium"
        >
          + Create Advertiser
        </button>
      </div>

      {advertisers.length === 0 ? (
        <EmptyState
          title="No advertisers yet"
          body="Create one to start tracking paid campaigns."
        />
      ) : (
        <div className="bg-slate-800 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Company</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2 text-right">Campaigns</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Spend</th>
                <th className="px-3 py-2 text-right">ROI</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {advertisers.map((a) => {
                const totals = totalsByAdvertiser.get(a.id) ?? { revenue: 0, spend: 0 };
                const roi = totals.spend > 0 ? ((totals.revenue - totals.spend) / totals.spend) * 100 : 0;
                return (
                  <tr key={a.id} className="hover:bg-slate-700/50 cursor-pointer" onClick={() => onSelect(a.id)}>
                    <td className="px-3 py-2 text-white">{a.name}</td>
                    <td className="px-3 py-2 text-slate-300">{a.company_name ?? '\u2014'}</td>
                    <td className="px-3 py-2 text-slate-300 text-xs">{a.contact_email ?? '\u2014'}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{a.campaignCount}</td>
                    <td className="px-3 py-2 text-right text-green-400 tabular-nums">${totals.revenue.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-red-400 tabular-nums">${totals.spend.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{roi.toFixed(1)}%</td>
                    <td className="px-3 py-2"><StatusPill active={a.is_active} /></td>
                    <td className="px-3 py-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => startEdit(a)} className="text-xs text-primary hover:underline">Edit</button>
                      <button onClick={() => void handleToggle(a.id, a.is_active)} className="text-xs text-slate-400 hover:text-white">
                        {a.is_active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
